import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Loader2, Mail, ArrowLeft, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE } from "../lib/apiConfig";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // email | code | success
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const inputRefs = useRef([]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/send-reset-code`, { email });
      setStep("code");
      toast.success("Reset code sent! Check your email.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 6).split("");
      digits.forEach((d, i) => {
        if (i + index < 6) newCode[i + index] = d;
      });
      setCode(newCode);
      const nextIdx = Math.min(index + digits.length, 5);
      inputRefs.current[nextIdx]?.focus();
      return;
    }
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const fullCode = code.join("");
    if (fullCode.length !== 6) {
      toast.error("Please enter the full 6-digit code");
      return;
    }
    if (!password || password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/reset-password-with-code`, {
        email,
        code: fullCode,
        new_password: password,
      });
      setStep("success");
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Reset failed. Please check your code and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/send-reset-code`, { email });
      toast.success("New code sent!");
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch {
      toast.error("Failed to resend. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="fixed inset-0 paper-texture pointer-events-none" />
      <header className="p-4">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src="/icon-192.png" alt="Culture Ken" className="w-10 h-10 rounded-xl" />
          <span className="text-xl font-bold text-foreground">Culture Ken</span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg" data-testid="forgot-password-card">
          {step === "success" ? (
            <>
              <CardHeader className="text-center pb-4">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <CardTitle className="text-2xl font-bold">Password Reset!</CardTitle>
                <CardDescription className="text-base">
                  Your password has been updated. You can now sign in with your new password.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  className="w-full h-12 text-lg rounded-xl font-medium"
                  onClick={() => navigate("/login")}
                  data-testid="goto-login-after-reset"
                >
                  Sign In
                </Button>
              </CardContent>
            </>
          ) : step === "code" ? (
            <>
              <CardHeader className="space-y-2 text-center">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
                  <Mail className="w-7 h-7 text-primary" />
                </div>
                <CardTitle className="text-2xl font-bold">Enter Reset Code</CardTitle>
                <CardDescription className="text-base">
                  We sent a 6-digit code to <strong>{email}</strong>. Enter it below with your new password.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleResetPassword} className="space-y-5">
                  <div>
                    <Label className="text-base mb-2 block">Reset Code</Label>
                    <div className="flex justify-center gap-2" data-testid="reset-code-input-group">
                      {code.map((digit, i) => (
                        <Input
                          key={i}
                          ref={(el) => (inputRefs.current[i] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={digit}
                          onChange={(e) => handleCodeChange(i, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(i, e)}
                          className="w-12 h-14 text-center text-xl font-mono font-bold"
                          data-testid={`reset-code-input-${i}`}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new-password" className="text-base">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="At least 6 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-12 text-base pr-12"
                        data-testid="reset-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password" className="text-base">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="h-12 text-base"
                      data-testid="reset-confirm-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 text-lg rounded-xl font-medium"
                    disabled={loading}
                    data-testid="reset-submit-btn"
                  >
                    {loading ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Resetting...</>
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                </form>

                <div className="mt-4 text-center space-y-3">
                  <Button
                    variant="ghost"
                    className="text-sm"
                    onClick={handleResend}
                    disabled={resendLoading}
                    data-testid="resend-reset-code-btn"
                  >
                    {resendLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      "Resend Code"
                    )}
                  </Button>
                  <Button asChild variant="ghost" className="w-full h-10 rounded-xl gap-2">
                    <Link to="/login">
                      <ArrowLeft className="w-4 h-4" /> Back to Sign In
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="space-y-2 text-center">
                <CardTitle className="text-2xl font-bold">Forgot Password?</CardTitle>
                <CardDescription className="text-base">
                  Enter your email and we'll send you a 6-digit code to reset your password.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSendCode} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-base">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 text-base"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 text-lg rounded-xl font-medium"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      "Send Reset Code"
                    )}
                  </Button>
                  <Button asChild variant="ghost" className="w-full h-10 rounded-xl gap-2">
                    <Link to="/login">
                      <ArrowLeft className="w-4 h-4" /> Back to Sign In
                    </Link>
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}
